import { useCallback } from 'react'
import { useAvailableCountriesListQuery } from '@/api/available-countries-api'
import { UNLIMITED_COUNTRY_CODE } from '@/constants/country-code'
import { Condition } from '@/types/api/search'

type UseAvailableCountries = () => {
  isAvailableCountriesFetching: boolean
  isCountrySupported: (queryParams: Condition) => boolean
  getAvailableCountriesSearchStatus: (
    queryParams: Condition,
    isDefaultSearch: boolean,
    isEmptyResult: boolean,
  ) => {
    hasAvailableCountriesSearchResults: boolean
    isUnsupportedCountry: boolean
  }
}

const useAvailableCountries: UseAvailableCountries = () => {
  const { data, isFetching, isSuccess } = useAvailableCountriesListQuery()

  const isCountrySupported = useCallback(
    (queryParams: Condition) => {
      if (!queryParams.country_code) {
        return false
      }

      if (queryParams.country_code === UNLIMITED_COUNTRY_CODE) {
        return true
      }

      return isSuccess && data.countries.includes(queryParams.country_code)
    },
    [data, isSuccess],
  )

  const getAvailableCountriesSearchStatus = useCallback(
    (
      queryParams: Condition,
      isDefaultSearch: boolean,
      isEmptyResult: boolean,
    ): {
      hasAvailableCountriesSearchResults: boolean
      isUnsupportedCountry: boolean
    } => {
      const hasAvailableCountriesSearchResults =
        isCountrySupported(queryParams) && !(isDefaultSearch && isEmptyResult)
      const isUnsupportedCountry =
        !isFetching && !hasAvailableCountriesSearchResults && isSuccess
      return { hasAvailableCountriesSearchResults, isUnsupportedCountry }
    },
    [isCountrySupported, isFetching, isSuccess],
  )

  return {
    isCountrySupported,
    getAvailableCountriesSearchStatus,
    isAvailableCountriesFetching: isFetching,
  }
}

export default useAvailableCountries
